import { useMemo } from "react";

import { useTestProductModalContext } from "@/contexts/TestProductModalContext";
import { useOrganisationBasketContext } from "@/contexts/organisations/OrganisationBasketContext";
import { COMING_SOON } from "@/core/constants";
import { PanelBoxV2 } from "@/tpo/Boxes";
import Stack from "@/tpo/Stack";
import { useTheme } from "styled-components";

import AddOnDetail from "../../shared/ui/AddOnDetail";
import { AddonsHeader } from "./Addons";
import CyrexAddOnExpandedContent from "./CyrexAddOnExpandedContent";

const ORIGINAL_SAMPLE_ADDON_UNAVAILABLE =
  "This array has already been tested against this sample and therefore is unavailable.";

function useSelectedAddonIds(addons) {
  const { testProduct } = useTestProductModalContext();
  const { hasProductGotAddon } = useOrganisationBasketContext();
  return useMemo(
    () => new Set(addons.filter(a => hasProductGotAddon(testProduct.id, a.id)).map(a => a.id)),
    [addons, testProduct.id, hasProductGotAddon]
  );
}

function useOverlapAddonState(addons, selectedAddonIds, originalSampleAddonProductIds) {
  return useMemo(() => {
    const fromOriginalSample = new Set(originalSampleAddonProductIds || []);
    const combined = new Set([...selectedAddonIds, ...fromOriginalSample]);
    const overlappingDisabledIds = new Set();
    const overlapMessages = new Map();

    const nameForProductId = id => addons.find(a => a.id === id)?.name;

    for (const addon of addons) {
      if (selectedAddonIds.has(addon.id)) {
        continue;
      }
      const overlapping = addon.overlappingProductIds || [];
      const conflictId = overlapping.find(id => combined.has(id));
      if (conflictId) {
        overlappingDisabledIds.add(addon.id);
        const otherName = nameForProductId(conflictId);
        if (otherName) {
          overlapMessages.set(
            addon.id,
            `This array overlaps with ${otherName}. Only one of these options can be selected per sample.`
          );
        }
      }
    }
    return { overlappingDisabledIds, overlapMessages };
  }, [addons, selectedAddonIds, originalSampleAddonProductIds]);
}

function useCyrexAddonState(addon, disabled) {
  const { testProduct } = useTestProductModalContext();
  const { hasProductGotAddon } = useOrganisationBasketContext();

  if (disabled) return "unavailable";
  if (addon.status === COMING_SOON) return "unavailable";
  if (hasProductGotAddon(testProduct.id, addon.id)) return "selected";
  return "unselected";
}

function CyrexAddonItem({
  addon,
  parentDisabled,
  overlapDisabled,
  originalSampleDisabled,
  overlapMessage,
  onRemove,
  onAdd
}) {
  const rowDisabled = parentDisabled || overlapDisabled || originalSampleDisabled;
  const state = useCyrexAddonState(addon, rowDisabled);

  const overlapError = parentDisabled
    ? undefined
    : originalSampleDisabled
    ? ORIGINAL_SAMPLE_ADDON_UNAVAILABLE
    : overlapMessage;

  return (
    <AddOnDetail
      name={addon.name}
      description={addon.content.description}
      expandedContent={
        <CyrexAddOnExpandedContent
          description={addon.content.description}
          informationPanels={addon.content.informationPanels}
          biomarkersTested={addon.content.biomarkersTested}
        />
      }
      price={addon.tradeCurrentPrice}
      rrpPrice={addon.exvatFullPrice}
      state={state}
      text={addon.status === COMING_SOON ? addon.status : undefined}
      error={overlapError}
      onRemove={onRemove}
      onAdd={onAdd}
    />
  );
}

function CyrexRequiredAddonItem({ addon }) {
  const nameWithStatus =
    addon.status === COMING_SOON ? `${addon.name} (${COMING_SOON})` : addon.name;

  return (
    <AddOnDetail
      readOnly
      name={nameWithStatus}
      description={addon.content?.description}
      expandedContent={
        <CyrexAddOnExpandedContent
          description={addon.content?.description}
          informationPanels={addon.content?.informationPanels}
          biomarkersTested={addon.content?.biomarkersTested}
        />
      }
    />
  );
}

export default function CyrexAddons({
  addons,
  disabled,
  readOnly = false,
  headerTitle,
  originalSampleAddonProductIds = [],
  onRemovingAddon,
  onAddingOn,
  headerDescription
}) {
  const theme = useTheme();
  const selectedAddonIds = useSelectedAddonIds(addons);
  const { overlappingDisabledIds, overlapMessages } = useOverlapAddonState(
    addons,
    selectedAddonIds,
    originalSampleAddonProductIds
  );
  const originalSampleAddonProductIdSet = useMemo(
    () => new Set(originalSampleAddonProductIds || []),
    [originalSampleAddonProductIds]
  );

  const resolvedHeaderTitle =
    headerTitle ??
    (readOnly
      ? "The following arrays are included in this bundle"
      : "Select the arrays you wish to test");

  if (readOnly) {
    return (
      <PanelBoxV2
        maxWidth={768}
        outer={{
          bg: "haze",
          pt: theme.spacing.section.pt,
          pb: theme.spacing.section.pb,
          px: [20, 20, 40],
          "data-component-name": "CyrexAddons"
        }}
        stacked
        gap={40}
      >
        <AddonsHeader title={resolvedHeaderTitle} description={headerDescription ?? ""} />
        <Stack gap={16}>
          {addons.map(addon => (
            <CyrexRequiredAddonItem key={addon.id} addon={addon} />
          ))}
        </Stack>
      </PanelBoxV2>
    );
  }

  return (
    <PanelBoxV2
      maxWidth={768}
      outer={{
        bg: "haze",
        pt: theme.spacing.section.pt,
        pb: theme.spacing.section.pb,
        px: [20, 20, 40],
        "data-component-name": "CyrexAddons"
      }}
      stacked
      gap={40}
    >
      <AddonsHeader title={resolvedHeaderTitle} description={headerDescription} />
      <Stack gap={16}>
        {addons.map(addon => (
          <CyrexAddonItem
            key={addon.id}
            addon={addon}
            parentDisabled={disabled}
            overlapDisabled={overlappingDisabledIds.has(addon.id)}
            originalSampleDisabled={originalSampleAddonProductIdSet.has(addon.id)}
            overlapMessage={overlapMessages.get(addon.id)}
            onRemove={() => onRemovingAddon({ addon })}
            onAdd={() => onAddingOn({ addon })}
          />
        ))}
      </Stack>
    </PanelBoxV2>
  );
}
